// third-party
import axios from 'axios';

// project imports
import i18n from '@/i18n';

// types
import { ErrorCategory, IAppError } from './types';

// ==============================|| ERRORS - NORMALIZE ||============================== //
// Orice eroare brută (axios, Error, string, …) → intrare IAppError uniformă: sursă
// (FE/BE) + categorie (runtime / validation / server), mesajul EXACT (nealterat, pentru
// diagnostic), mesajul prietenos RO (pentru toast/utilizator) și detaliile tehnice
// (endpoint, status, requestId/traceId, payload brut, stack).

// Mesajele prietenoase sunt chei i18n (grupul `errors` — editabile din Gestionare texte),
// cu fallback hardcodat prin defaultValue: modulul de erori e montat DEASUPRA
// TranslationsProvider și trebuie să funcționeze inclusiv când i18n/fetch-ul de texte a
// eșuat — plasa de siguranță rămâne. Evaluate LA MOMENTUL erorii (nu la load), ca
// override-urile din BE aplicate la pornire să fie deja active.
const friendly = {
	runtime: (): string =>
		i18n.t('errors.friendlyRuntime', {
			defaultValue: 'A apărut o eroare neașteptată în aplicație.'
		}),
	validation: (): string =>
		i18n.t('errors.friendlyValidation', {
			defaultValue: 'Cererea nu a putut fi procesată. Verifică datele introduse.'
		}),
	server: (): string =>
		i18n.t('errors.friendlyServer', {
			defaultValue: 'A apărut o eroare de server. Încearcă din nou în câteva momente.'
		}),
	network: (): string =>
		i18n.t('errors.friendlyNetwork', {
			defaultValue: 'Nu s-a putut contacta serverul. Verifică conexiunea la internet.'
		}),
	timeout: (): string =>
		i18n.t('errors.friendlyTimeout', {
			defaultValue: 'Serverul nu a răspuns la timp. Încearcă din nou.'
		})
} as const;

const safeStringify = (v: unknown): string => {
	try {
		return JSON.stringify(v);
	} catch {
		return String(v);
	}
};

const messageOf = (e: unknown): string | undefined =>
	typeof e === 'string' ? e : ((e as { message?: string })?.message ?? safeStringify(e));

// `errors` poate fi array simplu SAU dicționarul ProblemDetails din BE
// (ValidationProblem → `errors: { câmp: [mesaje] }`) — ambele se aplatizează.
const joinErrors = (errors: unknown): string | undefined => {
	if (Array.isArray(errors)) {
		const parts = errors.map(messageOf).filter(Boolean);
		return parts.length ? parts.join(', ') : undefined;
	}
	if (errors && typeof errors === 'object') {
		const parts = Object.values(errors as Record<string, unknown>)
			.flatMap((v) => (Array.isArray(v) ? v : [v]))
			.map(messageOf)
			.filter(Boolean);
		return parts.length ? parts.join(', ') : undefined;
	}
	return undefined;
};

// pull the EXACT server message in priority order; undefined = payload-ul nu are mesaj
const extractPayloadMessage = (data: unknown): string | undefined => {
	if (data == null) return undefined;
	if (typeof data === 'string') return data.trim() ? data : undefined;
	if (typeof data === 'object') {
		const d = data as Record<string, unknown>;
		return (
			(typeof d.message === 'string' && d.message) ||
			(typeof d.error === 'string' && d.error) ||
			joinErrors(d.errors) ||
			(typeof d.detail === 'string' && d.detail) ||
			(typeof d.title === 'string' && d.title) ||
			undefined
		);
	}
	return undefined;
};

// traceId (ProblemDetails) din payload, altfel header-ele uzuale de corelare
const headerValue = (headers: unknown, name: string): string | undefined => {
	if (!headers || typeof headers !== 'object') return undefined;
	const h = headers as Record<string, unknown> & { get?: (n: string) => unknown };
	const value = typeof h.get === 'function' ? h.get(name) : h[name];
	return typeof value === 'string' && value ? value : undefined;
};

const requestIdOf = (data: unknown, headers: unknown): string | undefined => {
	if (data && typeof data === 'object') {
		const { traceId } = data as Record<string, unknown>;
		if (typeof traceId === 'string' && traceId) return traceId;
	}
	return headerValue(headers, 'x-request-id') ?? headerValue(headers, 'x-correlation-id');
};

export function normalizeError(err: unknown): IAppError {
	const timestamp = new Date().toISOString();

	// ----- BE: axios errors -----
	if (axios.isAxiosError(err)) {
		const { response, config } = err;
		const method = config?.method?.toUpperCase();
		const url = config?.url;

		// network / timeout — fără răspuns de la server
		if (!response) {
			const timedOut = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
			return {
				source: 'BE',
				category: 'server',
				title: timedOut ? 'Eroare BE — timeout' : 'Eroare BE — rețea',
				message: err.message || 'Network Error',
				friendlyMessage: timedOut ? friendly.timeout() : friendly.network(),
				statusText: 'Fără răspuns de la server',
				method,
				url,
				code: err.code,
				timestamp
			};
		}

		const data = response.data as unknown;
		const { status, statusText } = response;
		const payloadMessage = extractPayloadMessage(data);
		const message = payloadMessage || err.message || statusText || `Eroare ${status}`;
		const category: ErrorCategory = status >= 500 ? 'server' : 'validation';
		// validare: mesajul BE e cel informativ (localizat RO în aplicație); server:
		// generic — nu expunem utilizatorului mesaje interne de 5xx (rămân în detalii)
		const friendlyMessage =
			category === 'server' ? friendly.server() : payloadMessage || friendly.validation();
		const code =
			data &&
			typeof data === 'object' &&
			typeof (data as Record<string, unknown>).code === 'string'
				? ((data as Record<string, unknown>).code as string)
				: err.code;

		return {
			source: 'BE',
			category,
			title: `Eroare BE${status ? ` ${status}` : ''}`,
			message,
			friendlyMessage,
			status,
			statusText,
			method,
			url,
			code,
			requestId: requestIdOf(data, response.headers),
			payload: data,
			timestamp
		};
	}

	// ----- FE: everything else -----
	if (err instanceof Error) {
		return {
			source: 'FE',
			category: 'runtime',
			title: 'Eroare FE',
			message: err.message || String(err),
			friendlyMessage: friendly.runtime(),
			code: err.name,
			stack: err.stack,
			timestamp
		};
	}

	return {
		source: 'FE',
		category: 'runtime',
		title: 'Eroare FE',
		message: typeof err === 'string' ? err : safeStringify(err),
		friendlyMessage: friendly.runtime(),
		timestamp
	};
}
